import { gtag } from './gtag.js';
import { format } from './format.js';
class GA4 {
    isInitialized;
    currentMeasurementId;
    hasLoadedGA;
    constructor() {
        this.reset();
    }
    reset = () => {
        this.isInitialized = false;
        this.currentMeasurementId = null;
        this.hasLoadedGA = false;
    };
    gtag = (...args) => {
        gtag(...args);
    };
    loadGA = (GA_MEASUREMENT_ID, nonce, gtagUrl = 'https://www.googletagmanager.com/gtag/js') => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }
        if (!this.hasLoadedGA) {
            // Global Site Tag (gtag.js) - Google Analytics
            const script = document.createElement('script');
            script.async = true;
            script.src = `${gtagUrl}?id=${GA_MEASUREMENT_ID}`;
            if (nonce) {
                script.setAttribute('nonce', nonce);
            }
            document.body.appendChild(script);
            this.hasLoadedGA = true;
        }
    };
    toGtagOptions = (gaOptions) => {
        if (!gaOptions) {
            return null;
        }
        const mapFields = {
            // Old https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#cookieUpdate
            // New https://developers.google.com/analytics/devguides/collection/gtagjs/cookies-user-id#cookie_update
            cookieUpdate: 'cookie_update',
            cookieExpires: 'cookie_expires',
            cookieDomain: 'cookie_domain',
            cookieFlags: 'cookie_flags', // must be in set method?
            userId: 'user_id',
            clientId: 'client_id',
            anonymizeIp: 'anonymize_ip',
            // https://support.google.com/analytics/answer/2853546?hl=en#zippy=%2Cin-this-article
            contentGroup1: 'content_group1',
            contentGroup2: 'content_group2',
            contentGroup3: 'content_group3',
            contentGroup4: 'content_group4',
            contentGroup5: 'content_group5',
            // https://support.google.com/analytics/answer/9050852?hl=en
            allowAdFeatures: 'allow_google_signals',
            allowAdPersonalizationSignals: 'allow_ad_personalization_signals',
            nonInteraction: 'non_interaction',
            page: 'page_path',
            hitCallback: 'event_callback',
        };
        return Object.entries(gaOptions).reduce((prev, [key, value]) => {
            if (mapFields[key]) {
                // eslint-disable-next-line no-param-reassign
                prev[mapFields[key]] = value;
            }
            else {
                // eslint-disable-next-line no-param-reassign
                prev[key] = value;
            }
            return prev;
        }, {});
    };
    // noinspection JSUnusedGlobalSymbols
    initialize = (GA_MEASUREMENT_ID, options = {}) => {
        if (!GA_MEASUREMENT_ID) {
            throw new Error('Require GA_MEASUREMENT_ID');
        }
        const initConfigs = typeof GA_MEASUREMENT_ID === 'string' ? [{ trackingId: GA_MEASUREMENT_ID }] : GA_MEASUREMENT_ID;
        this.currentMeasurementId = initConfigs[0].trackingId;
        const { gaOptions, gtagOptions, nonce, gtagUrl, } = options;
        this.loadGA(this.currentMeasurementId, nonce, gtagUrl);
        if (!this.isInitialized) {
            this.gtag('js', new Date());
            initConfigs.forEach((config) => {
                const mergedGtagOptions = {
                    // @ts-ignore
                    ...this.toGtagOptions({ ...gaOptions, ...config.gaOptions }),
                    ...gtagOptions,
                    // @ts-ignore
                    ...config.gtagOptions,
                };
                if (Object.keys(mergedGtagOptions).length) {
                    this.gtag('config', config.trackingId, mergedGtagOptions);
                }
                else {
                    this.gtag('config', config.trackingId);
                }
            });
        }
        this.isInitialized = true;
    };
    set = (fieldsObject) => {
        if (!fieldsObject) {
            globalThis.console.warn('`fieldsObject` is required in .set()');
            return;
        }
        if (typeof fieldsObject !== 'object') {
            globalThis.console.warn('Expected `fieldsObject` arg to be an Object');
            return;
        }
        if (Object.keys(fieldsObject).length === 0) {
            globalThis.console.warn('empty `fieldsObject` given to .set()');
        }
        this.gaCommand('set', fieldsObject);
    };
    gaCommandSendEvent = (eventCategory, eventAction, eventLabel, eventValue, fieldsObject) => {
        this.gtag('event', eventAction, {
            event_category: eventCategory,
            event_label: eventLabel,
            value: eventValue,
            ...(fieldsObject && { non_interaction: fieldsObject.nonInteraction }),
            ...this.toGtagOptions(fieldsObject),
        });
    };
    gaCommandSendEventParameters = (...args) => {
        if (typeof args[0] === 'string') {
            // @ts-ignore
            this.gaCommandSendEvent(...args.slice(1));
        }
        else {
            const { eventCategory, eventAction, eventLabel, eventValue, hitType, ...rest } = args[0];
            this.gaCommandSendEvent(eventCategory, eventAction, eventLabel, eventValue, rest);
        }
    };
    gaCommandSendTiming = (timingCategory, timingVar, timingValue, timingLabel) => {
        this.gtag('event', 'timing_complete', {
            name: timingVar,
            value: timingValue,
            event_category: timingCategory,
            event_label: timingLabel,
        });
    };
    gaCommandSendPageview = (page, fieldsObject) => {
        if (fieldsObject && Object.keys(fieldsObject).length) {
            // @ts-ignore
            const { title, location, ...rest } = this.toGtagOptions(fieldsObject);
            this.gtag('event', 'page_view', {
                ...(page && { page_path: page }),
                ...(title && { page_title: title }),
                ...(location && { page_location: location }),
                ...rest,
            });
        }
        else if (page) {
            this.gtag('event', 'page_view', { page_path: page });
        }
        else {
            this.gtag('event', 'page_view');
        }
    };
    gaCommandSendPageviewParameters = (...args) => {
        if (typeof args[0] === 'string') {
            // @ts-ignore
            this.gaCommandSendPageview(...args.slice(1));
        }
        else {
            const { page, hitType, ...rest } = args[0];
            this.gaCommandSendPageview(page, rest);
        }
    };
    // https://developers.google.com/analytics/devguides/collection/analyticsjs/command-queue-reference#send
    gaCommandSend = (...args) => {
        const hitType = typeof args[0] === 'string' ? args[0] : args[0].hitType;
        switch (hitType) {
            case 'event':
                this.gaCommandSendEventParameters(...args);
                break;
            case 'pageview':
                this.gaCommandSendPageviewParameters(...args);
                break;
            case 'timing':
                // @ts-ignore
                this.gaCommandSendTiming(...args.slice(1));
                break;
            case 'screenview':
            case 'transaction':
            case 'item':
            case 'social':
            case 'exception':
                globalThis.console.warn(`Unsupported send command: ${hitType}`);
                break;
            default:
                globalThis.console.warn(`Send command doesn't exist: ${hitType}`);
        }
    };
    gaCommandSet = (...args) => {
        if (typeof args[0] === 'string') {
            // eslint-disable-next-line no-param-reassign
            args[0] = { [args[0]]: args[1] };
        }
        this.gtag('set', this.toGtagOptions(args[0]));
    };
    gaCommand = (command, ...args) => {
        switch (command) {
            case 'send':
                this.gaCommandSend(...args);
                break;
            case 'set':
                this.gaCommandSet(...args);
                break;
            default:
                globalThis.console.warn(`Command doesn't exist: ${command}`);
        }
    };
    event = (optionsOrName, params) => {
        if (typeof optionsOrName === 'string') {
            this.gtag('event', optionsOrName, this.toGtagOptions(params));
        }
        else {
            const { action, category, label, value, nonInteraction, transport, } = optionsOrName;
            if (!category || !action) {
                globalThis.console.warn('args.category AND args.action are required in event()');
                return;
            }
            // Required Fields
            const fieldObject = {
                hitType: 'event',
                eventCategory: format(category),
                eventAction: format(action),
            };
            // Optional Fields
            if (label) {
                // @ts-ignore
                fieldObject.eventLabel = format(label);
            }
            if (typeof value !== 'undefined') {
                if (typeof value !== 'number') {
                    globalThis.console.warn('Expected `args.value` arg to be a Number.');
                }
                else {
                    // @ts-ignore
                    fieldObject.eventValue = value;
                }
            }
            if (typeof nonInteraction !== 'undefined') {
                if (typeof nonInteraction !== 'boolean') {
                    globalThis.console.warn('`args.nonInteraction` must be a boolean.');
                }
                else {
                    // @ts-ignore
                    fieldObject.nonInteraction = nonInteraction;
                }
            }
            if (typeof transport !== 'undefined') {
                if (typeof transport !== 'string') {
                    globalThis.console.warn('`args.transport` must be a string.');
                }
                else {
                    if (['beacon', 'xhr', 'image'].indexOf(transport) === -1) {
                        globalThis.console.warn('`args.transport` must be either one of these values: `beacon`, `xhr` or `image`');
                    }
                    // @ts-ignore
                    fieldObject.transport = transport;
                }
            }
            this.gaCommand('send', fieldObject);
        }
    };
    send = (fieldObject) => {
        this.gaCommand('send', fieldObject);
    };
}
const ga4 = new GA4();
export { ga4 };
//# sourceMappingURL=ga4.js.map